import { useNavigate } from 'react-router-dom';
import './Appointments.scss';
import useNewRequestVM from './useAppointmentsVM';
import { Loader } from 'components/Loader/Loader';
import { Button } from 'components/Button/Button';
import { Formik } from 'formik';
import { Modal } from 'components/Modal/Modal';
import { Datalist } from 'components/Datalist/Datalist';
import { UserAppointmentFormSchema } from 'components/Headers/UserHeader/UserAppointmentFormSchema';
import { useSelector } from 'react-redux';
import { getDatalists } from 'features/datalist';

export type AppointmentsPageProps = {};

export const Appointments: React.FC<AppointmentsPageProps> = () => {
    const {
        appointments,
        formatDate,
        me,
        isApiRequestLoading,
        isComponentLoading,
        isUserCreateAppointmentModalOpen,
        setIsUserCreateAppointmentModalOpen,
        handleModalClose,
        handleCreateAppointment
    } = useNewRequestVM();
    const navigate = useNavigate();
    const { datalists } = useSelector(getDatalists);

    const statusMap: Record<string, string> = {
        accepted: 'Accepté',
        refused: 'Refusé',
        pending: "En attente d'une réponse de votre APPV"
    };

    if (isApiRequestLoading || isComponentLoading) {
        return (
            <main id="requests">
                <Loader />
            </main>
        );
    }

    return (
        <>
            <main id="appointments">
                <Button
                    label="Demander un nouveau rendez-vous"
                    variant="dark"
                    type="button"
                    className="previous-step-button"
                    onClick={() => setIsUserCreateAppointmentModalOpen(true)}
                />
                <div className="appointments-list">
                    {!appointments.length && <div className="empty-state">Aucune demande de rendez-vous n'a encore été faite</div>}
                    {appointments.map((appointment, index) => (
                        <div className="appointment" key={index}>
                            <div className="appointment-content">
                                <h2>Rendez-vous demandé le {formatDate(appointment.created_at)} </h2>
                                <div className="status">
                                    <span className={`appointment-status ${appointment.status}`}>{statusMap[appointment.status]}</span>
                                    {appointment.status === 'refused' && (
                                        <span className="appointment-status-comment">
                                            Votre APPV va vous contacter pour convenir d'un nouveau rendez-vous
                                        </span>
                                    )}
                                    {appointment.status === 'accepted' && (
                                        <span className="appointment-status-link" onClick={() => navigate(`/app/user/agenda`)}>
                                            Voir mon agenda
                                        </span>
                                    )}
                                </div>
                                <div className="appointment-project">
                                    <h3>Informations sur le rendez-vous demandé</h3>
                                    <p>Objet : {appointment.label}</p>
                                    <p>Commentaire : {appointment.observation}</p>
                                    <p>Type de communication : {appointment.communication_type}</p>
                                    <p>Début : {formatDate(appointment.started_at)}</p>
                                    <p>Fin : {formatDate(appointment.ended_at)}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </main>
            <Modal
                open={isUserCreateAppointmentModalOpen}
                setClose={handleModalClose}
                title="Demande de rendez-vous"
                yesText="Envoyer"
                noText="Annuler"
                onClose={handleModalClose}
                formId="userForm"
                isValidateSubmit
                onValidate={() => {
                    document.getElementById('userForm')?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                }}
            >
                <Formik
                    initialValues={{
                        label: '',
                        observation: '',
                        started_at: '',
                        started_at_date: '',
                        started_at_time: '09:00',
                        ended_at: '',
                        ended_at_date: '',
                        ended_at_time: '09:00',
                        communication_type: ''
                    }}
                    enableReinitialize={true}
                    validationSchema={UserAppointmentFormSchema(datalists || {})}
                    onSubmit={async (values, { setSubmitting }) => {
                        handleCreateAppointment(values);
                        setSubmitting(false);
                        handleModalClose();
                    }}
                >
                    {(props) => {
                        return (
                            <form id="userForm" onSubmit={props.handleSubmit} noValidate>
                                <div className="form">
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Nom du rendez-vous</label>
                                            <input
                                                type="text"
                                                name="label"
                                                placeholder="Nom du rendez-vous"
                                                onChange={(e) => {
                                                    props.handleChange(e);
                                                }}
                                                value={props.values.label}
                                            />
                                            {props.errors.label && props.touched.label && (
                                                <p className="error">{String(props.errors.label)}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Commentaire</label>
                                            <textarea
                                                name="observation"
                                                placeholder="Commentaire"
                                                rows={3}
                                                onChange={(e) => {
                                                    props.handleChange(e);
                                                }}
                                                value={props.values.observation}
                                            />
                                            {props.errors.observation && props.touched.observation && (
                                                <p className="error">{String(props.errors.observation)}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Date de début</label>
                                            <div className="datetime-inputs">
                                                <input
                                                    type="date"
                                                    name="started_at_date"
                                                    placeholder="Date de début"
                                                    onChange={(e) => {
                                                        const date = e.target.value;
                                                        props.setFieldValue('started_at_date', date);
                                                        props.setFieldValue(
                                                            'started_at',
                                                            `${date}T${props.values.started_at_time}:00+01:00`
                                                        );
                                                    }}
                                                    onBlur={props.handleBlur}
                                                    value={props.values.started_at_date || ''}
                                                />
                                                <select
                                                    name="started_at_time"
                                                    onChange={(e) => {
                                                        const time = e.target.value;
                                                        props.setFieldValue('started_at_time', time);
                                                        props.setFieldValue(
                                                            'started_at',
                                                            `${props.values.started_at_date}T${time}:00+01:00`
                                                        );
                                                    }}
                                                    onBlur={props.handleBlur}
                                                    value={props.values.started_at_time || '09:00'}
                                                >
                                                    {Array.from({ length: 9 }).flatMap((_, index) => {
                                                        const hour = index + 9;
                                                        return [
                                                            <option key={`${hour}:00`} value={`${hour.toString().padStart(2, '0')}:00`}>
                                                                {hour.toString().padStart(2, '0')}:00
                                                            </option>,
                                                            <option key={`${hour}:30`} value={`${hour.toString().padStart(2, '0')}:30`}>
                                                                {hour.toString().padStart(2, '0')}:30
                                                            </option>
                                                        ];
                                                    })}
                                                </select>
                                            </div>
                                            {props.errors.started_at && props.touched.started_at && (
                                                <p className="error">{String(props.errors.started_at)}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Date de fin</label>
                                            <div className="datetime-inputs">
                                                <input
                                                    type="date"
                                                    name="ended_at_date"
                                                    placeholder="Date de fin"
                                                    onChange={(e) => {
                                                        const date = e.target.value;
                                                        props.setFieldValue('ended_at_date', date);
                                                        props.setFieldValue(
                                                            'ended_at',
                                                            `${date}T${props.values.ended_at_time || '18:00'}:00+01:00`
                                                        );
                                                    }}
                                                    onBlur={props.handleBlur}
                                                    value={props.values.ended_at_date || ''}
                                                />
                                                <select
                                                    name="ended_at_time"
                                                    onChange={(e) => {
                                                        const time = e.target.value;
                                                        props.setFieldValue('ended_at_time', time);
                                                        props.setFieldValue('ended_at', `${props.values.ended_at_date}T${time}:00+01:00`);
                                                    }}
                                                    onBlur={props.handleBlur}
                                                    value={props.values.ended_at_time || '09:00'}
                                                >
                                                    {Array.from({ length: 9 }).flatMap((_, index) => {
                                                        const hour = index + 9;
                                                        return [
                                                            <option key={`${hour}:00`} value={`${hour.toString().padStart(2, '0')}:00`}>
                                                                {hour.toString().padStart(2, '0')}:00
                                                            </option>,
                                                            <option key={`${hour}:30`} value={`${hour.toString().padStart(2, '0')}:30`}>
                                                                {hour.toString().padStart(2, '0')}:30
                                                            </option>
                                                        ];
                                                    })}
                                                </select>
                                            </div>
                                            {props.errors.ended_at && props.touched.ended_at && (
                                                <p className="error">{String(props.errors.ended_at)}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Type de rendez-vous</label>
                                            <Datalist
                                                list="communication_types"
                                                name="communication_type"
                                                placeholder="Sélectionner un type de communication"
                                                onChange={(e) => {
                                                    props.handleChange(e);
                                                }}
                                                onBlur={props.handleBlur}
                                                value={props.values.communication_type}
                                            />
                                            {props.errors.communication_type && props.touched.communication_type && (
                                                <p className="error">{String(props.errors.communication_type)}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </form>
                        );
                    }}
                </Formik>
            </Modal>
        </>
    );
};
